/* eslint-disable indent */
import React from 'react';
import { makeStyles, Button, Icons } from '~/lib/bn-material-ui';
import { useOrdersDialogStateSelector, useOrdersDialogDispatch } from '../context/orders-dialog-context';

const useStyles = makeStyles(({ palette }) => ({
  subdued: {
    color: palette.gray[60],
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
  },
  cancelOrderButton: {
    '& svg': {
      marginRight: '0.5rem',
    },
  },
}));

export function OrdersDialogActionButtons() {
  const styles = useStyles();
  const dispatch = useOrdersDialogDispatch();
  const [specifyingNewOrder, initializationState, selectedOrderEntity, newDataEntered, createOrEditSirenAction, hasUnsavedChanges, unsavedChanges, approveSirenAction, selectedBarnOrdersEntity, errorMessagesByField] = useOrdersDialogStateSelector([
    'specifyingNewOrder',
    'initializationState',
    'selectedOrderEntity',
    'newDataEntered',
    'createOrEditSirenAction',
    'hasUnsavedChanges',
    'unsavedChanges',
    'approveSirenAction',
    'selectedBarnOrdersEntity',
    'errorMessagesByField',
  ]);

  const doneInitializing = initializationState === 'complete';

  if (specifyingNewOrder) {
    const requestOrderAction = selectedBarnOrdersEntity?.getAction('request-order') && !selectedBarnOrdersEntity?.getAction('create-order');
    const disabled = !doneInitializing || !newDataEntered || Object.keys(errorMessagesByField).length;

    return (
      <div className={styles.container}>
        <Button
          variant='contained'
          size='small'
          onClick={() => dispatch({ type: 'create-or-request-order' })}
          disabled={disabled}
        >
          {requestOrderAction ? 'Request Order' : 'Save'}
        </Button>
      </div>
    );
  }

  if (!selectedOrderEntity) {
    // we are not specifying a new order and we have not yet loaded our selected order
    return null;
  }

  const updateOrderAction = selectedOrderEntity.getAction('update-order');
  const closeOrderAction = selectedOrderEntity.getAction('close-order');
  const scheduleOrderAction = selectedOrderEntity.getAction('schedule-order');
  if (!updateOrderAction && approveSirenAction) {
    // only show the approve action when that is all you have available
    // admins can approve orders even when they can also edit them
    return (
      <div className={styles.container}>
        <Button
          variant='contained'
          size='small'
          onClick={() => dispatch({ type: 'approve-order' })}
          disabled={!doneInitializing}
        >
          Acknowledge
        </Button>
      </div>
    );
  }

  if (!closeOrderAction && !updateOrderAction && !scheduleOrderAction) {
    // cant update the order in any way!
    return null;
  }

  const { cancelText, updateText } = getUpdateCloseButtonText({ selectedOrderEntity, scheduleOrderAction });
  const selectedFeedId = unsavedChanges?.feedId || selectedOrderEntity?.getSubEntityByRel('https://api.binsentry.com/rel/feed')?.href;
  const cancelDisabled = Boolean(!doneInitializing || !createOrEditSirenAction);
  const saveDisabled = Boolean(cancelDisabled || !selectedFeedId ||
    // Allow users to save the order whenever a scheduleOrderAction/approveSirenAction exists even if they have not changed anything
    (!scheduleOrderAction && (!hasUnsavedChanges || !newDataEntered)) ||
    Object.keys(errorMessagesByField).length);
  const saveAndConfirmDisabled = cancelDisabled || Object.keys(errorMessagesByField).length;

  return (
    <div className={styles.container}>
      <Button
        variant='outlined'
        color='error'
        size='small'
        disabled={cancelDisabled}
        className={styles.cancelOrderButton}
        onClick={() => dispatch({ type: 'cancel-order' })}
      >
        <Icons.Delete fontSize='small' /> {cancelText}
      </Button>
      <Button
        variant='contained'
        size='small'
        onClick={() => dispatch({ type: 'update-order' })}
        disabled={saveDisabled}
      >
        {updateText}
      </Button>
      {selectedOrderEntity.properties.state === 'draft' && (
        <Button
          variant='contained'
          size='small'
          onClick={() => dispatch({ type: 'update-and-confirm-order' })}
          disabled={!!saveAndConfirmDisabled}>
            Save and Confirm
        </Button>
      )}
    </div>
  );
}

const getUpdateCloseButtonText = ({ selectedOrderEntity, scheduleOrderAction }) => {
  if (scheduleOrderAction) {
    return {
      updateText: selectedOrderEntity.properties.type === 'suggestion' ? 'Confirm Suggestion' : 'Confirm Request',
      cancelText: 'Dismiss',
    };
  }

  // We only call out orders as being requests in the buttons while they are still unconfirmed
  // this ends up meaning that only producers see these verbs
  const orderIsRequest = selectedOrderEntity.properties.type === 'request';
  const orderIsReady = selectedOrderEntity.properties.state === 'ready';
  if (orderIsRequest && orderIsReady) {
    return {
      updateText: 'Edit Request',
      cancelText: 'Cancel Request',
    };
  }

  const orderIsSuggestion = selectedOrderEntity.properties.type === 'suggestion';
  if (orderIsSuggestion && orderIsReady) {
    return {
      updateText: 'Confirm Suggestion',
      cancelText: 'Dismiss',
    };
  }

  const orderIsDraft = selectedOrderEntity.properties.state === 'draft';
  if (orderIsDraft) {
    return {
      updateText: 'Save Draft',
      cancelText: 'Cancel',
    };
  }

  return {
    updateText: 'Save',
    cancelText: 'Cancel',
  };
};
